import { useCreateStationeryDraftMutation } from './ProductDetailsPage.graphql.generated';
import { StationeryDraftFormat } from '@graphql/generated-base';
import { useCardsRouterContext } from '@apps/card/Card.routes';

import { useExistingDraftsWarningContext } from '../ExistingDraftsWarningProvider';
import { useCardTelemetry } from '@apps/card/Card.telemetry';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { cloneDeep } from 'lodash-es';
import type { ThemeJson } from '@apps/card/routes/CardCustomizer/CardCustomizer.types';

import type { Format, MinimalRequiredTemplateDataForDraft } from '../DesignsGallery/DesignsGallery.types';
import { useRestrictedInternationalShippingWarningContext } from '../RestrictedInternationalShippingWarningProvider';
import type { StationeryProduct } from '@shared/utils/print-price/types';

type UseCustomizePremiumDigitalCardArgs = {
  eventId?: string;
  currentProduct: StationeryProduct | undefined;
  supportedFormats: Format;
};

export const useCustomizePremiumDigitalCard = (args: UseCustomizePremiumDigitalCardArgs) => {
  const { eventId, supportedFormats, currentProduct } = args;
  const { existingDraftsWarningDialog, cardDraftCreated, pdpCustomizeNowClicked, restrictedInternationalShippingWarningDialog } = useCardTelemetry({});

  const { goToDigitalCustomizeDraft } = useCardsRouterContext();
  const { checkForExistingDraftOrCreateNew } = useExistingDraftsWarningContext();
  const { checkIfUserIsRestricted } = useRestrictedInternationalShippingWarningContext();

  const [createStationeryDraft, { loading }] = useCreateStationeryDraftMutation({
    refetchQueries: () => ['GetDigitalAndPaperDraftsByEventId', 'GetExistingEventStationeryDraftsWithTemplate'],
  });

  const handleOnCustomizePremiumDigitalCardClick = useEventCallback((template: MinimalRequiredTemplateDataForDraft, subtotalInMinorUnits?: number) => {
    checkIfUserIsRestricted(
      StationeryDraftFormat.digital,
      { themeId: template.themeId, category: template.category },
      () => {
        // This callback is called when the RestrictedInternationalShipping warning dialog is about to be shown.
        restrictedInternationalShippingWarningDialog.enter();
      },
      () => {
        checkForExistingDraftOrCreateNew(
          template.id,
          async existingDraftsCount => {
            try {
              pdpCustomizeNowClicked({ themeId: template.themeId, supportedFormats, format: 'premiumDigital' });
              const cardJSON = cloneDeep(template.themeJSON) as ThemeJson;

              if (!currentProduct) {
                throw new Error('Tried creating draft with no selected product.');
              }

              const response = await createStationeryDraft({
                variables: {
                  payload: {
                    eventId,
                    cardJSON,
                    stationeryTemplateId: template.id,
                    format: StationeryDraftFormat.digital,
                    priceConfigId: currentProduct.priceConfigId,
                  },
                },
              });
              const maybeDraftId = response.data?.createStationeryDraft.id;

              if (maybeDraftId) {
                cardDraftCreated({
                  draftId: maybeDraftId,
                  themeId: template.themeId,
                  stationeryTemplateCategory: template.category,
                  isFavorite: !!template.isFavorite,
                  page: 'pdpModal',
                  existingDraftsCount,
                  subtotalInMinorUnits,
                  isDigital: true,
                  isPremiumDesign: true,
                  format: 'premiumDigital',
                  cardWidthInches: template.widthInches,
                  cardHeightInches: template.heightInches,
                });
                goToDigitalCustomizeDraft(maybeDraftId);
              }
            } catch (error) {
            } finally {
            }
          },
          () => {
            // This callback is called when the warning dialog is about to be shown.
            existingDraftsWarningDialog.enter();
          },
          true,
        );
      },
    );
  });

  return {
    handleOnCustomizePremiumDigitalCardClick,
    loading,
  };
};
